import { useRef, useState, useCallback, useEffect } from 'react';
import type { WordToken } from './types';
import { reportCrash } from '../../utils/crash-reporter';

interface UseSpeechmarksOptions {
  tokens: WordToken[];
  isPlaying: boolean;
  getCurrentTime: () => number;
}

export function useSpeechmarks({
  tokens,
  isPlaying,
  getCurrentTime,
}: UseSpeechmarksOptions) {
  const [revealedCount, setRevealedCount] = useState<number>(0);
  const rafRef = useRef<number | null>(null);
  const timingEntriesRef = useRef<{ timeMs: number; upToIndex: number }[]>([]);

  const buildTimingEntries = useCallback(() => {
    const entries: { timeMs: number; upToIndex: number }[] = [];

    const timedIndices: { index: number; timeMs: number }[] = [];
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].wordStartMs != null && tokens[i].wordStartMs! >= 0) {
        timedIndices.push({ index: i, timeMs: tokens[i].wordStartMs! });
      }
    }

    if (timedIndices.length === 0) {
      entries.push({ timeMs: 0, upToIndex: tokens.length - 1 });
      return entries;
    }

    let lastTimedIdx = -1;
    for (const { index, timeMs } of timedIndices) {
      entries.push({ timeMs, upToIndex: index });
      lastTimedIdx = index;
    }

    if (lastTimedIdx < tokens.length - 1) {
      const lastTime = timedIndices[timedIndices.length - 1].timeMs;
      for (let i = lastTimedIdx + 1; i < tokens.length; i++) {
        const offset = (i - lastTimedIdx) * 250;
        entries.push({ timeMs: lastTime + offset, upToIndex: i });
      }
    }

    entries.sort((a, b) => a.timeMs - b.timeMs);
    return entries;
  }, [tokens]);

  useEffect(() => {
    if (isPlaying) {
      timingEntriesRef.current = buildTimingEntries();
      setRevealedCount(0);

      const tick = () => {
        try {
          const currentMs = getCurrentTime();
          const entries = timingEntriesRef.current;

          let maxIndex = -1;
          for (const entry of entries) {
            if (entry.timeMs <= currentMs) {
              maxIndex = Math.max(maxIndex, entry.upToIndex);
            } else {
              break;
            }
          }

          setRevealedCount(maxIndex + 1);
          rafRef.current = requestAnimationFrame(tick);
        } catch (err) {
          reportCrash({
            feature: 'speechmarks-tick',
            errorMessage: err instanceof Error ? err.message : String(err),
            errorStack: err instanceof Error ? err.stack : undefined,
            context: { tokenCount: tokens.length },
          });
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, buildTimingEntries, getCurrentTime]);

  const reset = useCallback(() => {
    setRevealedCount(0);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const isNodeRevealed = useCallback(
    (nodeIndex: number): boolean => {
      if (!isPlaying) return true;
      return nodeIndex < revealedCount;
    },
    [isPlaying, revealedCount]
  );

  return {
    isNodeRevealed,
    revealedCount,
    reset,
  };
}
